import { AxiosErrorResponse } from '@/@types/Errors';
import { IRegister } from '@/@types/Register';
import { useAuth } from '@/auth/Auth';
import { useToast } from '@/hooks/use-toast';
import { isValidFullName } from '@/utils/isValidFullName';
import { isStaging } from '@/utils/isStaging';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { z } from 'zod';

const createRegisterSchema = (t: any) =>
  z
    .object({
      email: z
        .string()
        .min(1, { message: t('common:required_field') })
        .email({ message: t('validation:invalid_email') }),
      password: z
        .string()
        .min(8, { message: t('validation:password_min_length') })
        .regex(/(?=.*[a-z])/, { message: t('validation:password_lowercase') })
        .regex(/(?=.*[A-Z])/, { message: t('validation:password_uppercase') })
        .regex(/(?=.*\d)/, { message: t('validation:password_number') })
        .regex(/(?=.*[^a-zA-Z0-9])/, {
          message: t('validation:password_special_char'),
        }),
      password_confirmation: z
        .string()
        .min(1, { message: t('common:required_field') }),
      name: z
        .string()
        .min(1, { message: t('common:required_field') })
        .regex(/^[a-zA-ZÀ-ÿ\u00C0-\u017F\s]+$/, {
          message: t('common:invalid_characters'),
        })
        .refine((val) => !/[^a-zA-ZÀ-ÿ\u00C0-\u017F\s]/.test(val), {
          message: t('common:invalid_characters'),
        })
        .refine(isValidFullName, {
          message: t('validation:full_name_required'),
        }),
      phone: z
        .string()
        .min(8, { message: t('validation:min_8') })
        .max(15, { message: t('validation:max_15') }),
      phone_prefix: z.string().min(1, { message: t('common:required_field') }),
    })
    .refine((data) => data.password === data.password_confirmation, {
      path: ['password_confirmation'],
      message: t('validation:passwords_do_not_match'),
    });

export const useRegister = () => {
  const { signUp, fetchUserData } = useAuth();
  const { t, i18n } = useTranslation(['common', 'validation']);
  const location = useLocation();
  const { toast } = useToast();

  const RegisterSchema = createRegisterSchema(t);

  const inviteEmail = (location.state as any)?.inviteEmail as string | undefined;
  const inviteToken = (location.state as any)?.inviteToken as string | undefined;
  const isEmailLocked = !!inviteEmail;

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    getValues,
    watch,
    formState: { errors },
  } = useForm<IRegister>({
    resolver: zodResolver(RegisterSchema),
    defaultValues: {
      email: inviteEmail || '',
      phone: '',
      phone_prefix: '',
    },
  });

  const defaultValues = watch();

  const handleRegistrationSuccess = async () => {
    localStorage.setItem('welcomeModalOpen', JSON.stringify({ isOpen: true }));

    const from = location.state?.from as string | undefined;

    if (inviteToken) {
      localStorage.setItem('redirectAfterEmailVerification', `/invitation-accept/${inviteToken}`);
    } else if (from) {
      localStorage.setItem('redirectAfterEmailVerification', from);
    }

    await fetchUserData();

    const canSkipVerification = isStaging && !inviteToken;
    const redirectUrl = canSkipVerification ? (from || '/dashboard') : '/verify-email';
    window.location.href = redirectUrl;
  };

  const handleRegistrationError = (error: AxiosError<AxiosErrorResponse>) => {
    if (error.response?.status === 422) {
      Object.values(error.response.data.errors).forEach(
        (errorMessages: any) => {
          toast({
            variant: 'error',
            description: errorMessages[0],
          });
        },
      );
      return;
    }

    toast({
      variant: 'error',
      description: error?.message,
    });
  };

  const {
    mutate,
    isPending: isRegistering,
    isError,
    isSuccess,
  } = useMutation({
    mutationFn: async (data: IRegister): Promise<void> => {
      const searchParams = new URLSearchParams(location.search);
      const referralCode = searchParams.get('ref');

      const languagePreference =
        (i18n.resolvedLanguage || i18n.language || 'pt').split('-')[0];

      const payload = {
        ...data,
        terms: true,
        language_preference: languagePreference,
        ...(inviteToken ? { invite_token: inviteToken } : {}),
      };

      await signUp(payload, referralCode);
    },
    onSuccess: handleRegistrationSuccess,
    onError: handleRegistrationError,
  });

  const onSubmit = (data: IRegister) => mutate(data);

  useEffect(() => {
    if (defaultValues?.phone) {
      clearErrors('phone');
    }
  }, [defaultValues?.phone, clearErrors]);

  useEffect(() => {
    const currentPrefix = getValues('phone_prefix');
    if (currentPrefix) return;

    const browserLang =
      typeof navigator !== 'undefined' ? navigator.language : '';
    const baseLang = (browserLang || '').split('-')[0]?.toLowerCase();

    const prefixByLang: Record<string, string> = {
      pt: '55',
      en: '1',
      es: '34',
      fr: '33',
      de: '49',
      it: '39',
      ja: '81',
    };

    const prefix = prefixByLang[baseLang] ?? '55';
    setValue('phone_prefix', prefix, { shouldValidate: true });
  }, [getValues, setValue]);

  return {
    onSubmit,
    handleSubmit,
    setValue,
    register,
    errors,
    isRegistering,
    isError,
    isSuccess,
    defaultValues,
    isEmailLocked,
  };
};
